import { Link, useParams } from "@tanstack/solid-router";
import { For, Show, createMemo, createSignal, onMount } from "solid-js";

import { AppShell } from "~/components/AppShell";
import { AskWorkspaceWidget } from "~/components/AskWorkspaceWidget";
import { EntityTimelineFeed } from "~/components/EntityTimelineFeed";
import { HotLeadsWidget } from "~/components/HotLeadsWidget";
import { MyRecentChangesWidget } from "~/components/MyRecentChangesWidget";
import { RecentAIActivityWidget } from "~/components/RecentAIActivityWidget";
import { ReviewQueueWidget } from "~/components/ReviewQueueWidget";
import { useActivities, useDashboard, useMe, useWorkspace } from "~/lib/hooks";
import { readRecentViews, type RecentView } from "~/lib/recentViews";

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency === "—" ? "USD" : currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

function formatMonth(yyyymm: string): string {
  if (yyyymm === "unscheduled") return "Unscheduled";
  // YYYY-MM → "Jul 2026". Day-01 keeps the local-time parse stable across
  // timezones; we only care about the month label.
  const [y, m] = yyyymm.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  if (Number.isNaN(d.getTime())) return yyyymm;
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

export function WorkspaceHomePage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };
  const me = useMe();
  const workspace = useWorkspace(() => params().wsKey);
  const dashboard = useDashboard(() => params().wsKey);

  // Most-recent activities across all entities. Sized to the widget.
  const recent = useActivities(
    () => params().wsKey,
    () => ({ limit: 10 }),
  );

  const myFirstName = createMemo(() => {
    const dn = me.data?.display_name;
    if (dn) return dn.split(" ")[0];
    return me.data?.email?.split("@")[0] ?? "there";
  });

  // Recently viewed is localStorage-backed, so read it on mount. We re-read
  // when the user navigates to/from a detail page via location-change is
  // overkill; refresh on each home-page mount is enough.
  const [recents, setRecents] = createSignal<RecentView[]>([]);
  onMount(() => setRecents(readRecentViews(params().wsKey, 8)));

  return (
    <AppShell wsKey={params().wsKey} pageTitle="Home">
      <div class="flex flex-col gap-4">
        <div>
          <h1 class="text-2xl font-bold">Welcome, {myFirstName()}</h1>
          <Show when={workspace.data}>
            <p class="text-sm opacity-70 mt-1">
              You're in <strong>{workspace.data!.name}</strong>.
            </p>
          </Show>
        </div>

        <AskWorkspaceWidget wsKey={() => params().wsKey} />

        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <TasksCard wsKey={() => params().wsKey} />
          <DealsCard wsKey={() => params().wsKey} />
          <ClosedDealsCard wsKey={() => params().wsKey} />
          <MyCountsCard wsKey={() => params().wsKey} />
        </div>

        <ReviewQueueWidget wsKey={() => params().wsKey} />

        <div class="grid gap-4 md:grid-cols-2">
          <HotLeadsWidget wsKey={() => params().wsKey} />
          <RecentAIActivityWidget wsKey={() => params().wsKey} />
        </div>

        <MyRecentChangesWidget wsKey={() => params().wsKey} />

        <Show when={recents().length > 0}>
          <div class="card bg-base-100 shadow-sm">
            <div class="card-body">
              <h2 class="card-title text-lg">Recently viewed</h2>
              <ul class="grid gap-1 mt-2 md:grid-cols-2">
                <For each={recents()}>
                  {(rv) => {
                    const link =
                      rv.entity_type === "account"
                        ? {
                            to: "/workspaces/$wsKey/accounts/$accId" as const,
                            params: { wsKey: params().wsKey, accId: rv.id },
                          }
                        : rv.entity_type === "contact"
                          ? {
                              to: "/workspaces/$wsKey/contacts/$conId" as const,
                              params: { wsKey: params().wsKey, conId: rv.id },
                            }
                          : {
                              to: "/workspaces/$wsKey/deals/$dealId" as const,
                              params: { wsKey: params().wsKey, dealId: rv.id },
                            };
                    return (
                      <li>
                        <Link
                          to={link.to}
                          params={link.params}
                          class="block hover:bg-base-200 rounded-md p-2 transition"
                        >
                          <div class="flex items-center gap-2">
                            <span class="badge badge-ghost badge-sm">{rv.entity_type}</span>
                            <span class="font-medium truncate flex-1">{rv.name}</span>
                          </div>
                          <Show when={rv.subtitle}>
                            <div class="text-xs opacity-60 truncate ml-1">{rv.subtitle}</div>
                          </Show>
                        </Link>
                      </li>
                    );
                  }}
                </For>
              </ul>
            </div>
          </div>
        </Show>

        <div class="card bg-base-100 shadow-sm">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <h2 class="card-title text-lg">Recent activity</h2>
              <Show when={recent.data}>
                <span class="text-xs opacity-60">Last {recent.data!.items.length}</span>
              </Show>
            </div>
            <Show when={recent.isLoading}>
              <div class="py-4 text-center text-sm opacity-60">Loading…</div>
            </Show>
            <Show when={recent.isError}>
              <div role="alert" class="alert alert-error text-sm">
                Failed to load recent activity.
              </div>
            </Show>
            <Show when={recent.data}>
              <Show
                when={recent.data!.items.length > 0}
                fallback={
                  <div class="py-6 text-center opacity-60 text-sm">
                    No activity yet. Log a meeting, call, or task on a contact or account to start
                    the feed.
                  </div>
                }
              >
                <EntityTimelineFeed wsKey={() => params().wsKey} items={() => recent.data!.items} />
              </Show>
            </Show>
          </div>
        </div>
      </div>
    </AppShell>
  );

  function TasksCard(_props: { wsKey: () => string }) {
    return (
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <h2 class="card-title text-lg">My tasks</h2>
            <Link
              to="/workspaces/$wsKey/tasks"
              params={{ wsKey: params().wsKey }}
              class="link link-primary text-xs"
            >
              View all →
            </Link>
          </div>
          <Show when={dashboard.isLoading}>
            <div class="py-3 text-sm opacity-60">Loading…</div>
          </Show>
          <Show when={dashboard.data}>
            {(data) => {
              const t = data().tasks;
              return (
                <div class="grid grid-cols-3 gap-2 mt-2">
                  <StatTile
                    label="Open"
                    value={t.my_planned}
                    tone={t.my_planned > 0 ? "primary" : "muted"}
                  />
                  <StatTile
                    label="Due today"
                    value={t.my_due_today}
                    tone={t.my_due_today > 0 ? "warning" : "muted"}
                  />
                  <StatTile
                    label="Overdue"
                    value={t.my_overdue}
                    tone={t.my_overdue > 0 ? "error" : "muted"}
                  />
                </div>
              );
            }}
          </Show>
        </div>
      </div>
    );
  }

  function DealsCard(_props: { wsKey: () => string }) {
    return (
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <h2 class="card-title text-lg">Pipeline</h2>
            <Link
              to="/workspaces/$wsKey/deals"
              params={{ wsKey: params().wsKey }}
              class="link link-primary text-xs"
            >
              View board →
            </Link>
          </div>
          <Show when={dashboard.isLoading}>
            <div class="py-3 text-sm opacity-60">Loading…</div>
          </Show>
          <Show when={dashboard.data}>
            {(data) => {
              const d = data().deals;
              return (
                <>
                  <div class="flex items-baseline gap-3 mt-2">
                    <div class="text-3xl font-bold">{d.open_count}</div>
                    <div class="text-sm opacity-70">open deals</div>
                  </div>
                  <Show when={d.open_total_by_currency.length > 0}>
                    <div class="flex flex-wrap gap-2 mt-2">
                      <For each={d.open_total_by_currency}>
                        {(c) => (
                          <span class="badge badge-outline">
                            {formatMoney(c.total_amount, c.currency)}
                          </span>
                        )}
                      </For>
                    </div>
                    <div
                      class="flex flex-wrap items-center gap-2 mt-2"
                      title="Forecast = sum of (amount × probability%) across open deals"
                    >
                      <span class="text-xs uppercase opacity-50">Forecast</span>
                      <For each={d.open_total_by_currency}>
                        {(c) => (
                          <span class="badge badge-primary badge-outline">
                            {formatMoney(c.forecast_amount, c.currency)}
                          </span>
                        )}
                      </For>
                    </div>
                  </Show>
                  <Show when={d.forecast_by_month.length > 0}>
                    <div class="mt-3" title="Weighted forecast bucketed by expected-close month">
                      <div class="text-xs uppercase opacity-50 mb-1">By month</div>
                      <ul class="space-y-0.5">
                        <For each={d.forecast_by_month.slice(0, 6)}>
                          {(m) => (
                            <li class="flex items-center gap-2 text-xs">
                              <span class="opacity-60 w-20 truncate">{formatMonth(m.month)}</span>
                              <span class="flex-1 font-medium">
                                {formatMoney(m.weighted_amount, m.currency)}
                              </span>
                              <span class="opacity-50">
                                {m.count} deal{m.count === 1 ? "" : "s"}
                              </span>
                            </li>
                          )}
                        </For>
                      </ul>
                    </div>
                  </Show>
                  <Show when={d.default_pipeline_name && d.by_stage.length > 0}>
                    <div class="mt-3">
                      <div class="text-xs uppercase opacity-50 mb-1">{d.default_pipeline_name}</div>
                      <ul class="space-y-1">
                        <For each={d.by_stage}>
                          {(s) => (
                            <li class="flex items-center gap-2 text-sm">
                              <span
                                class={`badge badge-sm ${
                                  s.stage_type === "won"
                                    ? "badge-success"
                                    : s.stage_type === "lost"
                                      ? "badge-error"
                                      : "badge-outline"
                                }`}
                              >
                                {s.count}
                              </span>
                              <span class="flex-1 truncate">{s.stage_name}</span>
                              <Show when={s.total_amount > 0}>
                                <span
                                  class="opacity-60 text-xs"
                                  title={`Forecast: ${formatMoney(
                                    s.forecast_amount,
                                    d.open_total_by_currency[0]?.currency ?? "USD",
                                  )}`}
                                >
                                  {formatMoney(
                                    s.total_amount,
                                    d.open_total_by_currency[0]?.currency ?? "USD",
                                  )}
                                </span>
                              </Show>
                            </li>
                          )}
                        </For>
                      </ul>
                    </div>
                  </Show>
                  <Show when={d.stalled_count > 0}>
                    <Link
                      to="/workspaces/$wsKey/deals"
                      params={{ wsKey: params().wsKey }}
                      search={{ stalled: d.stalled_days }}
                      class="block mt-3 border-t border-base-200 pt-2 hover:bg-base-200 -mx-2 px-2 rounded-md transition"
                      title={`Open deals whose record hasn't been touched in ${d.stalled_days} days`}
                    >
                      <div class="flex items-center gap-2">
                        <span class="badge badge-warning badge-sm">{d.stalled_count}</span>
                        <span class="flex-1 text-sm">
                          stalled <span class="opacity-60">({d.stalled_days}d+ no movement)</span>
                        </span>
                        <span class="opacity-50 text-xs">→</span>
                      </div>
                      <Show when={d.stalled_total_by_currency.length > 0}>
                        <div class="flex flex-wrap gap-2 mt-1">
                          <For each={d.stalled_total_by_currency}>
                            {(t) => (
                              <span class="badge badge-warning badge-outline badge-sm">
                                {formatMoney(t.total_amount, t.currency)}
                              </span>
                            )}
                          </For>
                        </div>
                      </Show>
                    </Link>
                  </Show>
                </>
              );
            }}
          </Show>
        </div>
      </div>
    );
  }

  function ClosedDealsCard(_props: { wsKey: () => string }) {
    return (
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <h2 class="card-title text-lg">Recent results</h2>
          <Show when={dashboard.isLoading}>
            <div class="py-3 text-sm opacity-60">Loading…</div>
          </Show>
          <Show when={dashboard.data}>
            {(data) => {
              const c = data().closed_deals;
              const totalClosed = c.won_count + c.lost_count;
              return (
                <>
                  <p class="text-xs opacity-50 mt-1">Last {c.window_days} days</p>
                  <div class="flex items-baseline gap-4 mt-2">
                    <div>
                      <div class="text-3xl font-bold text-success">{c.won_count}</div>
                      <div class="text-xs opacity-70">won</div>
                    </div>
                    <div>
                      <div class="text-3xl font-bold text-error">{c.lost_count}</div>
                      <div class="text-xs opacity-70">lost</div>
                    </div>
                    <Show when={c.win_rate_pct !== null}>
                      <div class="ml-auto">
                        <div class="text-2xl font-bold">{c.win_rate_pct}%</div>
                        <div class="text-xs opacity-70">win rate</div>
                      </div>
                    </Show>
                  </div>
                  <Show when={c.avg_days_to_close !== null}>
                    <p
                      class="text-xs opacity-70 mt-2"
                      title="Average calendar days from deal creation to close"
                    >
                      Avg cycle <span class="font-medium">{c.avg_days_to_close} days</span>
                    </p>
                  </Show>
                  <Show when={totalClosed === 0}>
                    <p class="text-sm opacity-60 mt-2">No deals closed in this window yet.</p>
                  </Show>
                  <Show when={c.totals_by_currency.length > 0}>
                    <div class="mt-3 space-y-1">
                      <For each={c.totals_by_currency}>
                        {(t) => (
                          <div class="flex items-center gap-2 text-xs">
                            <span class="opacity-50 w-10">{t.currency}</span>
                            <span class="badge badge-success badge-sm badge-outline">
                              +{formatMoney(t.won_amount, t.currency)}
                            </span>
                            <Show when={t.lost_amount > 0}>
                              <span class="badge badge-error badge-sm badge-outline">
                                −{formatMoney(t.lost_amount, t.currency)}
                              </span>
                            </Show>
                          </div>
                        )}
                      </For>
                    </div>
                  </Show>
                </>
              );
            }}
          </Show>
        </div>
      </div>
    );
  }

  function MyCountsCard(_props: { wsKey: () => string }) {
    return (
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <h2 class="card-title text-lg">Owned by me</h2>
          <Show when={dashboard.isLoading}>
            <div class="py-3 text-sm opacity-60">Loading…</div>
          </Show>
          <Show when={dashboard.data}>
            {(data) => {
              const c = data().my_counts;
              return (
                <div class="grid grid-cols-3 gap-2 mt-2">
                  <CountLink
                    label="Accounts"
                    value={c.accounts}
                    to="/workspaces/$wsKey/accounts"
                    wsKey={params().wsKey}
                  />
                  <CountLink
                    label="Contacts"
                    value={c.contacts}
                    to="/workspaces/$wsKey/contacts"
                    wsKey={params().wsKey}
                  />
                  <CountLink
                    label="Open deals"
                    value={c.deals}
                    to="/workspaces/$wsKey/deals"
                    wsKey={params().wsKey}
                  />
                </div>
              );
            }}
          </Show>
        </div>
      </div>
    );
  }
}

type Tone = "primary" | "warning" | "error" | "muted";
function StatTile(props: { label: string; value: number; tone: Tone }) {
  const toneCls =
    props.tone === "primary"
      ? "text-primary"
      : props.tone === "warning"
        ? "text-warning"
        : props.tone === "error"
          ? "text-error"
          : "opacity-50";
  return (
    <div>
      <div class={`text-2xl font-bold ${toneCls}`}>{props.value}</div>
      <div class="text-xs uppercase opacity-60">{props.label}</div>
    </div>
  );
}

function CountLink(props: {
  label: string;
  value: number;
  to: string;
  wsKey: string;
}) {
  return (
    <Link
      to={props.to}
      params={{ wsKey: props.wsKey }}
      class="block hover:bg-base-200 rounded-md p-2 transition"
    >
      <div class="text-2xl font-bold">{props.value}</div>
      <div class="text-xs uppercase opacity-60">{props.label}</div>
    </Link>
  );
}
